import { formatDateTime } from '../utils/dates.ts'
import React from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  PlusIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  EyeIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
import { api } from '../services/api.ts'
import LoadingSpinner from '../components/LoadingSpinner.tsx'
import JobStatusBadge from '../components/JobStatusBadge.tsx'
import ConfirmDialog from '../components/ConfirmDialog.tsx'

export default function Jobs() {
  const queryClient = useQueryClient()
  const [page, setPage] = React.useState(0)
  const [statusFilter, setStatusFilter] = React.useState('')
  const [deleteDialog, setDeleteDialog] = React.useState<{
    isOpen: boolean
    jobId: string | null
    jobName: string
  }>({
    isOpen: false,
    jobId: null,
    jobName: '',
  })

  const { data: jobs, isLoading } = useQuery(
    ['jobs', page, statusFilter],
    () => {
      const params = new URLSearchParams({
        skip: String(page * 10),
        limit: '10',
      })
      if (statusFilter) {
        params.append('status', statusFilter)
      }
      return api.get(`/api/jobs/?${params.toString()}`)
    },
    {
      keepPreviousData: true,
      refetchInterval: (data) => {
        // Poll every 10 seconds if there are running jobs
        const hasRunningJobs = data?.data?.some(
          (job: any) => job.status === 'running'
        )
        return hasRunningJobs ? 10000 : false
      },
    }
  )

  const startJobMutation = useMutation(
    (jobId: string) => api.post(`/api/jobs/${jobId}/start`),
    {
      onSuccess: () => {
        toast.success('Job started successfully')
        queryClient.invalidateQueries('jobs')
        queryClient.invalidateQueries('all-jobs')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to start job')
      },
    }
  )

  const cancelJobMutation = useMutation(
    (jobId: string) => api.post(`/api/jobs/${jobId}/cancel`),
    {
      onSuccess: () => {
        toast.success('Job cancelled successfully')
        queryClient.invalidateQueries('jobs')
        queryClient.invalidateQueries('all-jobs')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to cancel job')
      },
    }
  )

  const deleteJobMutation = useMutation(
    (jobId: string) => api.delete(`/api/jobs/${jobId}`),
    {
      onSuccess: () => {
        toast.success('Job deleted successfully')
        queryClient.invalidateQueries('jobs')
        queryClient.invalidateQueries('all-jobs')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to delete job')
      },
    }
  )

  const handleStartJob = (jobId: string) => {
    startJobMutation.mutate(jobId)
  }

  const handleCancelJob = (jobId: string) => {
    cancelJobMutation.mutate(jobId)
  }

  const handleDeleteJob = (jobId: string, jobName: string) => {
    setDeleteDialog({
      isOpen: true,
      jobId,
      jobName,
    })
  }

  const confirmDeleteJob = () => {
    if (deleteDialog.jobId) {
      deleteJobMutation.mutate(deleteDialog.jobId)
      setDeleteDialog({
        isOpen: false,
        jobId: null,
        jobName: '',
      })
    }
  }

  const cancelDeleteJob = () => {
    setDeleteDialog({
      isOpen: false,
      jobId: null,
      jobName: '',
    })
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='sm:flex sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
            Jobs
          </h1>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Manage your scraping jobs
          </p>
        </div>
        <div className='mt-4 sm:mt-0'>
          <Link to='/jobs/create' className='btn-primary'>
            <PlusIcon className='h-4 w-4 mr-2' />
            Create Job
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className='bg-white shadow rounded-lg p-4 dark:bg-gray-800'>
        <div className='flex items-center space-x-4'>
          <div>
            <label
              htmlFor='status-filter'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              Status
            </label>
            <select
              id='status-filter'
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className='select mt-1'
            >
              <option value=''>All Statuses</option>
              <option value='pending'>Pending</option>
              <option value='running'>Running</option>
              <option value='completed'>Completed</option>
              <option value='failed'>Failed</option>
              <option value='cancelled'>Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className='bg-white shadow overflow-hidden sm:rounded-md dark:bg-gray-800'>
        <ul className='divide-y divide-gray-200'>
          {jobs?.data?.map((job: any) => (
            <li key={job.id}>
              <div className='px-4 py-4 sm:px-6'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center'>
                    <div className='flex-shrink-0'>
                      <JobStatusBadge status={job.status} />
                    </div>
                    <div className='ml-4'>
                      <div className='flex items-center'>
                        <p className='text-sm font-medium text-gray-900 dark:text-white'>
                          {job.state} - {job.keywords.length} keywords
                        </p>
                      </div>
                      <div className='mt-1'>
                        <p className='text-sm text-gray-500 dark:text-gray-400'>
                          Created:{' '}
                          {formatDateTime(job.created_at)}
                        </p>
                        {job.total_stores > 0 && (
                          <p className='text-sm text-gray-500'>
                            Stores found: {job.total_stores}
                          </p>
                        )}

                        {/* Cost Information */}
                        <div className='flex items-center space-x-4 mt-1'>
                          {job.estimated_cost_avg && (
                            <div className='flex items-center space-x-1'>
                              <CurrencyDollarIcon className='h-4 w-4 text-gray-400' />
                              <span className='text-sm text-gray-500'>
                                Est: ${job.estimated_cost_avg.toFixed(2)}
                              </span>
                            </div>
                          )}
                          {job.actual_cost !== null &&
                            job.actual_cost !== undefined && (
                              <div className='flex items-center space-x-1'>
                                <CurrencyDollarIcon className='h-4 w-4 text-green-500' />
                                <span className='text-sm text-green-600 dark:text-green-400 font-medium'>
                                  Actual: ${job.actual_cost.toFixed(2)}
                                </span>
                              </div>
                            )}
                          {job.duplicates_removed > 0 && (
                            <div className='flex items-center space-x-1'>
                              <span className='text-sm text-yellow-600 dark:text-yellow-400'>
                                {job.duplicates_removed} duplicates
                              </span>
                            </div>
                          )}
                          {job.api_calls && (
                            <div className='text-xs text-gray-400'>
                              {job.api_calls.text_search || 0} searches,{' '}
                              {job.api_calls.place_details || 0} details
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <Link
                      to={`/jobs/${job.id}`}
                      className='text-blue-600 hover:text-blue-500'
                      title='View job details'
                    >
                      <EyeIcon className='h-5 w-5' />
                    </Link>

                    {(job.status === 'pending' ||
                      job.status === 'failed' ||
                      job.status === 'cancelled') && (
                      <button
                        onClick={() => handleStartJob(job.id)}
                        className='text-green-600 hover:text-green-500'
                        disabled={startJobMutation.isLoading}
                        title={
                          job.status !== 'pending'
                            ? 'Retry job (resumes from last checkpoint)'
                            : 'Start job'
                        }
                      >
                        <PlayIcon className='h-5 w-5' />
                      </button>
                    )}

                    {(job.status === 'pending' || job.status === 'running') && (
                      <button
                        onClick={() => handleCancelJob(job.id)}
                        className='text-yellow-600 hover:text-yellow-500'
                        disabled={cancelJobMutation.isLoading}
                        title='Cancel job'
                      >
                        <StopIcon className='h-5 w-5' />
                      </button>
                    )}

                    <button
                      onClick={() =>
                        handleDeleteJob(
                          job.id,
                          `${job.state} - ${job.keywords.length} keywords`
                        )
                      }
                      className='text-red-600 hover:text-red-500'
                      disabled={deleteJobMutation.isLoading}
                      title='Delete job'
                    >
                      <TrashIcon className='h-5 w-5' />
                    </button>
                  </div>
                </div>

                {job.progress && (
                  <div className='mt-4'>
                    <div className='flex items-center justify-between text-sm text-gray-500'>
                      <span>{job.progress.message}</span>
                      <span>{job.progress.percentage.toFixed(1)}%</span>
                    </div>
                    <div className='mt-2 bg-gray-200 rounded-full h-2'>
                      <div
                        className='bg-blue-600 h-2 rounded-full transition-all duration-300'
                        style={{ width: `${job.progress.percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>

        {jobs?.data?.length === 0 && (
          <div className='text-center py-12'>
            <BriefcaseIcon className='mx-auto h-12 w-12 text-gray-400' />
            <h3 className='mt-2 text-sm font-medium text-gray-900'>No jobs</h3>
            <p className='mt-1 text-sm text-gray-500'>
              Get started by creating a new scraping job.
            </p>
            <div className='mt-6'>
              <Link to='/jobs/create' className='btn-primary'>
                <PlusIcon className='h-4 w-4 mr-2' />
                Create Job
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {jobs?.data?.length > 0 && (
        <div className='bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 dark:bg-gray-800'>
          <div className='flex-1 flex justify-between sm:hidden'>
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className='btn-secondary disabled:opacity-50 dark:text-gray-500 hover:text-gray-700 '
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={jobs?.data?.length < 10}
              className='btn-secondary disabled:opacity-50 dark:text-gray-500 hover:text-gray-700 '
            >
              Next
            </button>
          </div>
          <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
            <div>
              <p className='text-sm text-gray-700 dark:text-gray-300'>
                Showing page <span className='font-medium'>{page + 1}</span>
              </p>
            </div>
            <div>
              <nav className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px gap-2'>
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={jobs?.data?.length < 10}
                  className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDeleteJob}
        onConfirm={confirmDeleteJob}
        title='Delete Job'
        message={`Are you sure you want to delete "${deleteDialog.jobName}"? This will also delete all associated stores and cannot be undone.`}
        confirmText='Delete'
        cancelText='Cancel'
        type='danger'
        isLoading={deleteJobMutation.isLoading}
      />
    </div>
  )
}
